import { StrictMode } from 'react';
import '@/lib/monaco';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import App from '@/App.tsx';
import '@/index.css';
import { ErrorView } from '@/views/ErrorView.tsx';
import { ScadViewer } from '@/views/ScadViewer.tsx';
import { ExamplesViewer } from '@/views/ExamplesViewer.tsx';
import { Explore } from '@/views/Explore.tsx';
import { LandingPage } from '@/views/LandingPage.tsx';
import { UserProfile } from '@/views/UserProfile.tsx';
import { PublicModelView } from '@/views/PublicModelView.tsx';
import { AuthCallback } from '@/views/AuthCallback.tsx';
import { BlogIndex } from '@/blog/BlogIndex.tsx';
import { BlogPost } from '@/blog/BlogPost.tsx';
import { Changelog } from '@/views/Changelog.tsx';
import { Features } from '@/views/Features.tsx';
import { Cheatsheet } from '@/views/Cheatsheet.tsx';
import { Pricing } from '@/views/Pricing.tsx';
import { Terms } from '@/views/Terms.tsx';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster.tsx';
import { TooltipProvider } from '@/components/ui/tooltip.tsx';
import { AuthProvider } from '@/contexts/AuthContext.tsx';
import { ThemeProvider } from '@/contexts/ThemeContext.tsx';
import { FavoriteColorsProvider } from '@/hooks/useFavoriteColors.tsx';
import { LazyMotion, domAnimation, MotionConfig } from 'framer-motion';
import { WebMCPProvider } from 'webmcp-react';

const queryClient = new QueryClient();

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <App />,
      errorElement: <ErrorView />,
      children: [
        // Home - Landing page
        {
          index: true,
          element: <LandingPage />,
          errorElement: <ErrorView />,
        },
        // New model editor
        {
          path: 'new',
          element: <ScadViewer />,
          errorElement: <ErrorView />,
        },
        // Examples viewer (index → first example, :id → specific example)
        {
          path: 'examples',
          element: <ExamplesViewer />,
          errorElement: <ErrorView />,
        },
        {
          path: 'examples/:id',
          element: <ExamplesViewer />,
          errorElement: <ErrorView />,
        },
        // Explore page
        {
          path: 'explore',
          element: <Explore />,
          errorElement: <ErrorView />,
        },
        // Settings page (placeholder)
        {
          path: 'settings',
          element: (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              Settings page coming soon
            </div>
          ),
          errorElement: <ErrorView />,
        },
        // Auth callback (for OAuth)
        {
          path: 'auth/callback',
          element: <AuthCallback />,
          errorElement: <ErrorView />,
        },
        // Changelog
        {
          path: 'changelog',
          element: <Changelog />,
          errorElement: <ErrorView />,
        },
        // Features
        {
          path: 'features',
          element: <Features />,
          errorElement: <ErrorView />,
        },
        // Cheatsheet
        {
          path: 'cheatsheet',
          element: <Cheatsheet />,
          errorElement: <ErrorView />,
        },
        // Pricing
        {
          path: 'pricing',
          element: <Pricing />,
          errorElement: <ErrorView />,
        },
        // Terms of Service
        {
          path: 'terms',
          element: <Terms />,
          errorElement: <ErrorView />,
        },
        // Blog index
        {
          path: 'blog',
          element: <BlogIndex />,
          errorElement: <ErrorView />,
        },
        // Blog post
        {
          path: 'blog/:slug',
          element: <BlogPost />,
          errorElement: <ErrorView />,
        },
        // User profile - /:username
        {
          path: ':username',
          element: <UserProfile />,
          errorElement: <ErrorView />,
        },
        // Model view - /:username/:slug
        {
          path: ':username/:slug',
          element: <PublicModelView />,
          errorElement: <ErrorView />,
        },
        // Model version view - /:username/:slug/v/:version
        {
          path: ':username/:slug/v/:version',
          element: <PublicModelView />,
          errorElement: <ErrorView />,
        },
      ],
    },
  ],
  { future: { v7_relativeSplatPath: true }, basename: '/playground' },
);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <WebMCPProvider name="Meshpit" version="1.0.0">
      <LazyMotion features={domAnimation}>
        <MotionConfig reducedMotion="user">
          <ThemeProvider>
            <FavoriteColorsProvider>
              <QueryClientProvider client={queryClient}>
                <AuthProvider>
                  <TooltipProvider delayDuration={0}>
                    <Toaster />
                    <RouterProvider
                      router={router}
                      future={{ v7_startTransition: true }}
                    />
                  </TooltipProvider>
                </AuthProvider>
              </QueryClientProvider>
            </FavoriteColorsProvider>
          </ThemeProvider>
        </MotionConfig>
      </LazyMotion>
    </WebMCPProvider>
  </StrictMode>,
);
